import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import defaultContent from "../../content/earningsCalculator";
import commonDefaultContent from "../../content/common";
import { IconInfoCircle } from "../../common/icons/IconInfoCircle/IconInfoCircle";
import { LeafToggleSwitch } from "../../common/leaf/LeafToggleSwitch/LeafToggleSwitch";
import LeafLinkButton from "../../common/leaf/LeafLinkButton/LeafLinkButton";
import { useAppDispatch, useAppSelector } from "../../store";
import { isProgramActive } from "../../common/util/checkProgramActiveStatus";
import { AWARD_CODES } from "../../common/enums/award-codes";
import {
  activateBBITab,
  activateBFITab,
  initializeSettings,
  updateBronzeTarget,
  updateTrainingMode,
} from "../../reducers/earningCalculator";
import { storedLocale } from "../../components/Authentication/util";
import { getBronzeAwardCodes } from "../../common/util/bronzeAwardCodes";
import { getLoginAffAbo } from "../../reducers/model/boot";
import { useGetECSettingsQuery } from "../../services/EarningsCalculator/earningCalculatorAPI";
import {
  bronzeBuilderTargetTrack,
  bronzeFoundationTargetTrack,
  openABOLegsInfoPanelTrack,
  openECInfoPanelTrack,
  trackECPage,
  trainingModeOffTrack,
  trainingModeOnTrack,
} from "../../common/tealium/EarningCalculator/EarningCalculatorTrack";
import LeafFilterButton from "../../common/leaf/LeafFilter/LeafFilterButton/LeafFilterButton";
import EarningsCalculatorLoader from "./EarningsCalculatorLoader/EarningsCalculatorLoader";
import EarningsCalculatorABOLegs from "./EarningsCalculatorABOLegs/EarningsCalculatorABOLegs";
import EarningsCalculatorProgressTile from "./EarningsCalculatorProgressTile/EarningsCalculatorProgressTile";
import { EarningsCalculatorExceedMaxBanner } from "./EarningsCalculatorExceedMaxBanner/EarningsCalculatorExceedMaxBanner";
import EarningsCalculatorDashbaord from "./EarningsCalculatorDashboard/EarningsCalculatorDashboard";
import EarningsCalculatorInfoSlider from "./EarningsCalculatorInfoSlider/EarningsCalculatorInfoSlider";
import { useECCalculate } from "./EarningsCalculatorData/useECCalculate";
import { BONUS_KPI } from "./EarningsCalculatorIncomeBreakdown/EarningsCalculatorIncomeBreakdown";
import "./EarningsCalculator.scss";
import { IconCheck } from "../../common/icons/IconCheck/IconCheck";

const EarningsCalculator = () => {
  const { t } = useTranslation(["earningsCalculator", "common", "myBusinessIncome"]);

  const dispatch = useAppDispatch();
  const boot = useAppSelector(({ boot }) => boot);
  const { ECComputedData, IsTrainingMode, Initialized, IsBFITabActive, IsBBITabActive } = useAppSelector(
    (state) => state.earningCalculator,
  );

  const { Settings, StaticData } = ECComputedData;
  const { IsExceededMaxValue } = Settings;
  const { IsBFIQualified, IsBBIQualified } = StaticData;

  const [showInfoModal, setShowInfoModal] = useState(false);

  const { configuration, shellConfig, user } = boot || {};
  const { eligibleIncentives } = shellConfig || {};
  const { corePlus } = configuration || {};
  const { isoCountryCode = "" } = user || {};
  const { isoCurrencyCode = "" } = shellConfig || {};
  const { isStandardRetailEnhancementCountry = false } = corePlus || {};

  const isCSIActive = isProgramActive(eligibleIncentives, AWARD_CODES.CSI);
  const isBFIActive = isProgramActive(eligibleIncentives, AWARD_CODES.BRONZE_FOUNDATION);
  const isBBIActive = isProgramActive(eligibleIncentives, AWARD_CODES.BRONZE_BUILDER);

  const locale = storedLocale();
  const affAbo = getLoginAffAbo({ boot });

  const { bfi, bfiRestart1, bfiRestart2, bfiConsistencyPayment, bbi, bbiRestart1, bbiRestart2, bbiConsistencyPayment } =
    getBronzeAwardCodes(isoCountryCode);

  const {
    data: settingsAPIData,
    isFetching: isSettingsAPIFetching,
    status,
  } = useGetECSettingsQuery(
    {
      AffAbo: affAbo,
      Locale: locale,
      CountryCode: isoCountryCode,
      ISOCurrencyCode: isoCurrencyCode,
      BFI: bfi,
      BFIRestart1: bfiRestart1,
      BFIRestart2: bfiRestart2,
      BFIFixedPayment: bfiConsistencyPayment,
      BBI: bbi,
      BBIRestart1: bbiRestart1,
      BBIRestart2: bbiRestart2,
      BBIFixedPayment: bbiConsistencyPayment,
      IsCSIActive: isCSIActive,
      IsStandarRetailEnhancementCountry: isStandardRetailEnhancementCountry,
      EnableTrainingMode: IsTrainingMode,
    },
    { skip: Initialized },
  );

  // Call the calculate API with a debounce.
  const { isLoading: isCalculateAPIFetching } = useECCalculate();

  useEffect(() => {
    trackECPage();
  }, []);

  useEffect(() => {
    if (status === "fulfilled" && settingsAPIData) {
      dispatch(initializeSettings({ settingsAPIData }));
    }
  }, [isSettingsAPIFetching, settingsAPIData, dispatch, status]);

  const toggleSwitch = () => {
    IsTrainingMode === false ? trainingModeOnTrack() : trainingModeOffTrack();
    dispatch(updateTrainingMode());
  };

  const handleShowInfoModal = () => {
    setShowInfoModal(true);
    openECInfoPanelTrack();
  };

  const handleLearnMoreClick = () => {
    setShowInfoModal(true);
    openABOLegsInfoPanelTrack();
  };

  const handleInfoModalClose = () => {
    setShowInfoModal(false);
  };

  const handleBFITabClick = () => {
    if (IsBFIQualified === false && IsBFITabActive === false) {
      dispatch(activateBFITab());
      dispatch(updateBronzeTarget(BONUS_KPI.BFI));
      bronzeFoundationTargetTrack();
    }
  };

  const handleBBITabClick = () => {
    if (IsBBIQualified === false && IsBBITabActive === false) {
      dispatch(activateBBITab());
      dispatch(updateBronzeTarget(BONUS_KPI.BBI));
      bronzeBuilderTargetTrack();
    }
  };

  if (isSettingsAPIFetching === true) return <EarningsCalculatorLoader isLoading={true} />;

  return (
    <div className="earnings-calculator">
      <EarningsCalculatorExceedMaxBanner />
      <div className="earnings-calculator__header">
        <EarningsCalculatorInfoSlider
          title={`${t("infoSlider.header", defaultContent["infoSlider"]["header"] as string)}`}
          show={showInfoModal}
          onCloseClick={handleInfoModalClose}
          id={"earnings-calculator-info-slider"}
          IsLoading={isSettingsAPIFetching}
        />
        <PageTitleSection
          title={`${t("pageTitle", defaultContent["pageTitle"])}`}
          hideTimeStamp
          infoButton={
            <button
              aria-label={`${t("moreInfo", commonDefaultContent["moreInfo"], { ns: "common" })}`}
              className="earnings-calculator__header-info-button"
              id="infoModalButton"
              onClick={handleShowInfoModal}
            >
              <IconInfoCircle />
            </button>
          }
        />
        {IsExceededMaxValue === false && (
          <div className="earnings-calculator__header__toggle-switch">
            <div className="earnings-calculator__header__toggle-label">{`${t(
              "trainingMode",
              defaultContent["trainingMode"],
            )}`}</div>
            <div>
              <LeafToggleSwitch
                isToggled={IsTrainingMode}
                onToggle={toggleSwitch}
                name={`${t("trainingMode", defaultContent["trainingMode"])}`}
                toggleOnIcon={<IconCheck color="blue" />}
              />
            </div>
          </div>
        )}
      </div>
      <div className="earnings-calculator__content">
        <EarningsCalculatorDashbaord
          isFetching={isSettingsAPIFetching || isCalculateAPIFetching}
          isCSIActive={isCSIActive}
          isBFIActive={isBFIActive}
          isBBIActive={isBBIActive}
        />
        <EarningsCalculatorProgressTile
          isLoading={isSettingsAPIFetching}
          isCSIActive={isCSIActive}
          disableInput={isCalculateAPIFetching}
        />
        <div className="earnings-calculator__abo-legs">
          {/** ABO Legs component here */}
          <div className="earnings-calculator__abo-legs__header">
            <div className="earnings-calculator__abo-legs__title">
              <h5>{`${t("aboLegs.title", defaultContent["aboLegs"]["title"])}`}</h5>
              <div className="earnings-calculator__abo-legs__header-description">
                <div>{`${t("aboLegs.description", defaultContent["aboLegs"]["description"])}`}</div>
                <div>
                  <LeafLinkButton
                    theme="blue"
                    fontWeight="bold"
                    underlined={true}
                    onClick={handleLearnMoreClick}
                    as={"link"}
                    label={`${t("aboLegs.learnMore", defaultContent["aboLegs"]["learnMore"])}`}
                  >{`${t("aboLegs.learnMore", defaultContent["aboLegs"]["learnMore"])}`}</LeafLinkButton>
                </div>
              </div>
            </div>
            <div className="earnings-calculator__abo-legs__nav-tabs">
              <ul>
                {isBFIActive === true && (
                  <li>
                    <LeafFilterButton
                      text={""}
                      active={IsBFITabActive}
                      disabled={IsBFIQualified || isCalculateAPIFetching}
                      onButtonClick={handleBFITabClick}
                      ariaLabel={`${t(
                        "aboLegs.tabs.bronzeFoundation",
                        defaultContent["aboLegs"]["tabs"]["bronzeFoundation"],
                      )}`}
                    >
                      <span className="button_dot button_dot--foundation"></span>
                      <span>{`${t(
                        "aboLegs.tabs.bronzeFoundation",
                        defaultContent["aboLegs"]["tabs"]["bronzeFoundation"],
                      )}`}</span>
                    </LeafFilterButton>
                  </li>
                )}
                {isBBIActive === true && (
                  <li>
                    <LeafFilterButton
                      text={""}
                      active={IsBBITabActive}
                      disabled={IsBBIQualified || isCalculateAPIFetching}
                      onButtonClick={handleBBITabClick}
                      ariaLabel={`${t(
                        "aboLegs.tabs.bronzeBuilder",
                        defaultContent["aboLegs"]["tabs"]["bronzeBuilder"],
                      )}`}
                    >
                      <span className="button_dot button_dot--builder"></span>
                      <span>{`${t(
                        "aboLegs.tabs.bronzeBuilder",
                        defaultContent["aboLegs"]["tabs"]["bronzeBuilder"],
                      )}`}</span>
                    </LeafFilterButton>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <EarningsCalculatorABOLegs IsLoading={isSettingsAPIFetching} disableInput={isCalculateAPIFetching} />
        </div>
      </div>
      <div className="earnings-calculator__footer">
        <Trans
          i18nKey={"footer"}
          ns="earningsCalculator"
          defaults={defaultContent["footer"]}
          components={{
            br: <br />,
          }}
        ></Trans>
        <span>{`${t("footer", commonDefaultContent["footer"], { ns: "common" })}`}</span>
      </div>
    </div>
  );
};

export default EarningsCalculator;
